import { buildActiveContext } from '@baserow/modules/copilot/services/activeContextBuilder'
export const state = () => ({
  conversations: [],
  activeConversationId: null,
  messagesByConversation: {},
  status: 'idle',
  error: null,
  nudges: [],
  pendingClarification: null,
})

export const getters = {
  activeConversation(state) {
    return state.conversations.find(
      (c) => c.id === state.activeConversationId
    )
  },
  activeMessages(state) {
    if (!state.activeConversationId) {
      return []
    }
    return (
      state.messagesByConversation[state.activeConversationId] || []
    )
  },
}

export const mutations = {
  SET_CONVERSATIONS(state, conversations) {
    state.conversations = conversations
  },
  SET_ACTIVE_CONVERSATION(state, id) {
    state.activeConversationId = id
  },
  SET_MESSAGES_FOR_CONVERSATION(state, { conversationId, messages }) {
    state.messagesByConversation = {
      ...state.messagesByConversation,
      [conversationId]: messages,
    }
    const lastAssistant = [...messages]
      .reverse()
      .find((message) => message.role === 'assistant')
    if (
      lastAssistant?.metadata?.needs_clarification &&
      Array.isArray(lastAssistant.metadata.candidates)
    ) {
      state.pendingClarification = {
        conversationId,
        question: lastAssistant.metadata.question,
        candidates: lastAssistant.metadata.candidates,
      }
    } else if (state.pendingClarification?.conversationId === conversationId) {
      state.pendingClarification = null
    }
  },
  APPEND_MESSAGE(state, { conversationId, message }) {
    const existing =
      state.messagesByConversation[conversationId] || []
    state.messagesByConversation = {
      ...state.messagesByConversation,
      [conversationId]: [...existing, message],
    }
  },
  UPDATE_MESSAGE_STREAM(state, { conversationId, messageId, patch }) {
    const existing =
      state.messagesByConversation[conversationId] || []
    const index = existing.findIndex((m) => m.id === messageId)
    if (index === -1) {
      return
    }
    const updated = {
      ...existing[index],
      ...patch,
    }
    const copy = existing.slice()
    copy.splice(index, 1, updated)
    state.messagesByConversation = {
      ...state.messagesByConversation,
      [conversationId]: copy,
    }
  },
  SET_STATUS(state, status) {
    state.status = status
  },
  SET_ERROR(state, error) {
    if (error == null) {
      state.error = null
      return
    }
    state.error = {
      message: error.message != null ? String(error.message) : String(error),
      code: error.response?.data?.error ?? null,
    }
  },
  SET_NUDGES(state, nudges) {
    state.nudges = nudges
  },
  SET_PENDING_CLARIFICATION(state, pendingClarification) {
    state.pendingClarification = pendingClarification
  },
}

function getAssistantErrorKey(error) {
  const code = error?.response?.data?.error
  if (code === 'VOKALIS_ASSISTANT_NOT_CONFIGURED') {
    return 'copilot.assistantNotConfigured'
  }
  if (code === 'VOKALIS_ASSISTANT_DISABLED') {
    return 'copilot.assistantDisabled'
  }
  if (code === 'VOKALIS_ASSISTANT_LLM_ERROR') {
    return 'copilot.assistantLlmError'
  }
  return 'copilot.backendNotConnected'
}

export const actions = {
  async bootstrap({ commit, rootState }) {
    commit('SET_STATUS', 'loading')
    try {
      const workspaceId = rootState.workspace?.selected?.id
      if (!workspaceId) {
        commit('SET_CONVERSATIONS', [])
        commit('SET_STATUS', 'idle')
        return
      }
      const list = await this.app.$copilotChat.listConversations(workspaceId)
      commit('SET_CONVERSATIONS', list)
      commit('SET_STATUS', 'idle')
    } catch (error) {
      commit('SET_ERROR', error)
      commit('SET_STATUS', 'error')
    }
  },

  async ensureActiveConversation({ state, commit, dispatch }) {
    if (state.activeConversationId) {
      return state.activeConversationId
    }
    try {
      const contextSnapshot = buildActiveContext(this)
      const { id } = await this.app.$copilotChat.startConversation(contextSnapshot)
      const conversation = {
        id,
        title: 'Chat',
        lastMessageAt: new Date().toISOString(),
      }
      commit('SET_CONVERSATIONS', [...state.conversations, conversation])
      commit('SET_ACTIVE_CONVERSATION', id)
      commit('SET_MESSAGES_FOR_CONVERSATION', { conversationId: id, messages: [] })
      return id
    } catch (error) {
      const code = error?.response?.data?.error
      if (
        code === 'VOKALIS_ASSISTANT_NOT_CONFIGURED' ||
        code === 'VOKALIS_ASSISTANT_DISABLED'
      ) {
        commit('SET_ERROR', error)
        throw error
      }
      commit('SET_ERROR', error)
      const localId = `local-${Date.now()}`
      const conversation = {
        id: localId,
        title: 'Chat',
        lastMessageAt: new Date().toISOString(),
      }
      commit('SET_CONVERSATIONS', [...state.conversations, conversation])
      commit('SET_ACTIVE_CONVERSATION', localId)
      commit('SET_MESSAGES_FOR_CONVERSATION', { conversationId: localId, messages: [] })
      return localId
    }
  },

  async sendUserMessage(
    { state, commit, dispatch },
    { content, clarificationChoice = null }
  ) {
    const conversationId = await dispatch('ensureActiveConversation').catch(
      (error) => {
        commit('SET_ERROR', error)
        commit('SET_STATUS', 'error')
        const placeholderMessage = {
          id: `local-placeholder-${Date.now()}`,
          role: 'assistant',
          contentType: 'markdown',
          content: getAssistantErrorKey(error),
          createdAt: new Date().toISOString(),
        }
        commit('APPEND_MESSAGE', {
          conversationId: state.activeConversationId || 'local-error',
          message: placeholderMessage,
        })
        return null
      }
    )
    if (!conversationId) {
      return
    }
    commit('SET_ERROR', null)
    const contextSnapshot = buildActiveContext(this)

    const provisionalMessage = {
      id: `local-msg-${Date.now()}`,
      role: 'user',
      contentType: 'markdown',
      content,
      createdAt: new Date().toISOString(),
    }

    commit('APPEND_MESSAGE', { conversationId, message: provisionalMessage })
    commit('SET_STATUS', 'loading')
    commit('SET_PENDING_CLARIFICATION', null)

    if (conversationId.startsWith('local-')) {
      const placeholderMessage = {
        id: `local-placeholder-${Date.now()}`,
        role: 'assistant',
        contentType: 'markdown',
        content: 'copilot.backendNotConnected',
        createdAt: new Date().toISOString(),
      }
      commit('APPEND_MESSAGE', { conversationId, message: placeholderMessage })
      commit('SET_STATUS', 'idle')
      return
    }

    try {
      const result = await this.app.$copilotChat.sendUserMessage(conversationId, {
        content,
        context: contextSnapshot,
        clarificationChoice,
      })
      if (result && result.messages && result.messages.length) {
        commit('SET_MESSAGES_FOR_CONVERSATION', {
          conversationId,
          messages: result.messages,
        })
      }
      commit('SET_STATUS', 'idle')
    } catch (error) {
      commit('SET_ERROR', error)
      commit('SET_STATUS', 'error')
      const placeholderMessage = {
        id: `local-placeholder-${Date.now()}`,
        role: 'assistant',
        contentType: 'markdown',
        content: getAssistantErrorKey(error),
        createdAt: new Date().toISOString(),
      }
      commit('APPEND_MESSAGE', {
        conversationId,
        message: placeholderMessage,
      })
    }
  },

  async chooseClarification({ dispatch }, candidate) {
    const label =
      candidate.menu_label ||
      candidate.table_name ||
      candidate.solution_label ||
      candidate.candidate_id
    await dispatch('sendUserMessage', {
      content: label,
      clarificationChoice: { candidate_id: candidate.candidate_id },
    })
  },

  setNudges({ commit }, nudges) {
    commit('SET_NUDGES', nudges)
  },
  clearNudges({ commit }) {
    commit('SET_NUDGES', [])
  },
  async acceptNudge({ dispatch }, nudge) {
    const content = nudge.prompt || nudge.label || ''
    if (!content) {
      return
    }
    await dispatch('sendUserMessage', { content })
  },
}

export default {
  namespaced: true,
  state,
  getters,
  mutations,
  actions,
}
